import React from 'react';
import useClockTime from '../useClockTime';

const CX = 150, CY = 150;
const TICK_OUTER = 128;
const NUMERAL_R = 110;

const ticks = Array.from({ length: 60 }, (_, i) => {
  const angle = (i * 6 - 90) * Math.PI / 180;
  const isHour = i % 5 === 0;
  const isQuarter = i % 15 === 0;
  const innerR = isQuarter ? TICK_OUTER - 16 : isHour ? TICK_OUTER - 12 : TICK_OUTER - 6;
  return {
    x1: CX + TICK_OUTER * Math.cos(angle),
    y1: CY + TICK_OUTER * Math.sin(angle),
    x2: CX + innerR * Math.cos(angle),
    y2: CY + innerR * Math.sin(angle),
    strokeWidth: isQuarter ? 3 : isHour ? 2 : 0.8,
  };
});

const numerals = Array.from({ length: 12 }, (_, i) => {
  const num = i + 1;
  const angle = (num * 30 - 90) * Math.PI / 180;
  return { num, x: CX + NUMERAL_R * Math.cos(angle), y: CY + NUMERAL_R * Math.sin(angle) };
});

const MarbleWhite = () => {
  const { hourDeg, minuteDeg, secondDeg } = useClockTime();

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 300, height: 300 }}>
      <svg width={300} height={300} viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="mw-face" cx="35%" cy="30%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="55%" stopColor="#f5f0e8" />
            <stop offset="100%" stopColor="#ede4d4" />
          </radialGradient>
          <radialGradient id="mw-bezel" cx="40%" cy="35%">
            <stop offset="0%" stopColor="#e8c96a" />
            <stop offset="40%" stopColor="#c8a84b" />
            <stop offset="100%" stopColor="#9a7a2c" />
          </radialGradient>
          {/* Marble texture lines (faint) */}
          <filter id="mw-noise">
            <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
            <feBlend in="SourceGraphic" mode="multiply" result="blend" />
            <feComposite in="blend" in2="SourceGraphic" operator="in" />
          </filter>
        </defs>

        {/* Shadow drop */}
        <circle cx={CX + 2} cy={CY + 3} r="146" fill="rgba(0,0,0,0.12)" />

        {/* Gold bezel */}
        <circle cx={CX} cy={CY} r="146" fill="url(#mw-bezel)" />
        <circle cx={CX} cy={CY} r="146" fill="none" stroke="#f0d88a" strokeWidth="1" />
        <circle cx={CX} cy={CY} r="140" fill="none" stroke="#9a7a2c" strokeWidth="1" />

        {/* Marble face */}
        <circle cx={CX} cy={CY} r="136" fill="url(#mw-face)" />

        {/* Subtle marble vein lines */}
        <path d="M 100 80 Q 140 120 160 180 Q 175 210 200 230"
          stroke="#d8cfc0" strokeWidth="1" fill="none" opacity="0.4" />
        <path d="M 80 160 Q 110 150 140 170 Q 170 190 190 160"
          stroke="#d8cfc0" strokeWidth="0.8" fill="none" opacity="0.3" />

        {/* Inner bezel ring */}
        <circle cx={CX} cy={CY} r="136" fill="none" stroke="#c8a84b" strokeWidth="1.5" />
        <circle cx={CX} cy={CY} r="133" fill="none" stroke="#e8d8b0" strokeWidth="0.5" />

        {/* Tick marks */}
        {ticks.map((t, i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke="#4a4035" strokeWidth={t.strokeWidth} strokeLinecap="round" />
        ))}

        {/* Numerals */}
        {numerals.map(({ num, x, y }) => (
          <text key={num} x={x} y={y + 4.5} textAnchor="middle"
            fontSize="12.5" fontFamily="'Palatino Linotype', Palatino, Georgia, serif"
            fill="#3a3028" fontWeight="500" letterSpacing="0.5">
            {num}
          </text>
        ))}

        {/* Hour hand */}
        <g transform={`rotate(${hourDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 15} x2={CX} y2={CY - 74}
            stroke="#2c2820" strokeWidth="5.5" strokeLinecap="round" />
        </g>

        {/* Minute hand */}
        <g transform={`rotate(${minuteDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 17} x2={CX} y2={CY - 100}
            stroke="#2c2820" strokeWidth="3" strokeLinecap="round" />
        </g>

        {/* Second hand */}
        <g transform={`rotate(${secondDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 22} x2={CX} y2={CY - 108}
            stroke="#b03020" strokeWidth="1.2" strokeLinecap="round" />
        </g>

        {/* Center cap */}
        <circle cx={CX} cy={CY} r="5.5" fill="#c8a84b" />
        <circle cx={CX} cy={CY} r="2.5" fill="#3a3028" />
      </svg>
    </div>
  );
};

export default MarbleWhite;
